import { Container, Fade, Grid, Menu, MenuItem } from '@material-ui/core';
import { useOktaAuth } from '@okta/okta-react';
import {
  Box,
  RoleGuard,
  TopBar,
  TopBarV2,
  useHasPermission,
} from 'portal-commons';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { GERMANY_EVENTS_ENABLED, S3_ASSETS_PATH } from '@/constants';
import {
  BRANDS_DEFAULT_ENTRY,
  MY_CAMPAIGNS_HU_DEFAULT_ENTRY,
} from '@/constants/paths';
import { useAuthContext } from '@/contexts';
import { clearLocalStorage } from '@/utils/storage';

const MARKET_CONFIGS = {
  DE: {
    useTopBarV2: true,
    getMenuItems: (history) => [
      {
        key: 'brands',
        label: 'Brands',
        onClick: () => history.push(BRANDS_DEFAULT_ENTRY),
      },
      ...(GERMANY_EVENTS_ENABLED
        ? [
            {
              key: 'events',
              label: 'Events',
              onClick: () => history.push('/events'),
            },
          ]
        : []),
    ],
    showProfile: true,
    profileLabel: 'Profile',
  },
  HU: {
    useTopBarV2: true,
    getMenuItems: (history) => [
      {
        key: 'programs',
        label: 'My Programs',
        onClick: () => history.push(MY_CAMPAIGNS_HU_DEFAULT_ENTRY),
      },
    ],
    showProfile: false,
  },
  DEFAULT: {
    useTopBarV2: false,
    getMenuItems: () => [],
    showProfile: true,
    profileLabel: 'View CSP Profile',
  },
};

const Header = (props) => {
  const { title, note, testId } = props;
  const { roles, profile } = useAuthContext();
  const { oktaAuth } = useOktaAuth();
  const history = useHistory();
  const location = useLocation();
  const [flag, setFlag] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const active = profile?.status.toLowerCase() === 'active';
  const canViewProfile = useHasPermission('profile.view');
  const isRegistrationFlow = location.pathname.startsWith('/user/csp');

  const market = profile?.market || 'DEFAULT';
  const config = MARKET_CONFIGS[market] || MARKET_CONFIGS.DEFAULT;

  const handleLogout = useCallback(() => {
    clearLocalStorage();
    sessionStorage.setItem('logoutSuccess', true);
    oktaAuth.signOut('/login');
  }, [oktaAuth]);

  const menus = useMemo(() => {
    const TcrMenuItem = config.useTopBarV2
      ? TopBarV2.MenuItem
      : TopBar.MenuItem;
    const marketItems = config.getMenuItems(history).map((item) => (
      <TcrMenuItem key={item.key} onClick={item.onClick}>
        {item.label}
      </TcrMenuItem>
    ));

    const profileItem =
      config.showProfile && active && canViewProfile ? (
        <TcrMenuItem key="profile" onClick={() => history.push('/profile')}>
          {config.profileLabel || 'View CSP Profile'}
        </TcrMenuItem>
      ) : null;

    const logoutItem = (
      <TcrMenuItem key="logout" onClick={handleLogout}>
        Logout
      </TcrMenuItem>
    );

    return [...marketItems, profileItem, logoutItem].filter(Boolean);
  }, [history, active, handleLogout, canViewProfile, profile?.market]);

  const userName = localStorage.getItem('okta-token-storage')
    ? JSON.parse(localStorage.getItem('okta-token-storage')).idToken
      ? JSON.parse(localStorage.getItem('okta-token-storage')).idToken.claims
          .name
      : localStorage.getItem('userInfo')
      ? JSON.parse(localStorage.getItem('userInfo')).name
      : ''
    : '';

  const handleClick = (event) => {
    const anchorEl = event.currentTarget;
    setFlag((prevState) => !prevState);
    setAnchorEl(anchorEl);
  };

  const handleClose = () => {
    setFlag((prevState) => !prevState);
    setAnchorEl(null);
  };

  return isMobile ? (
    <Fragment>
      <header data-testid={testId}>
        <Container maxWidth={false} className="flex-centered">
          <Grid
            container
            spacing={0}
            alignItems="center"
            className="flex-centered"
          >
            {isMobile && (
              <Grid
                item
                lg={2}
                xs={isMobile ? 12 : 2}
                style={isMobile ? { height: '100%' } : {}}
              >
                {isMobile && (
                  <div className="header-logo">
                    <Link to="/" data-testid="headerLogoLink">
                      <img
                        src={`${S3_ASSETS_PATH}/images/logo/sideNavLogo.svg`}
                        alt="logo"
                      />
                    </Link>
                  </div>
                )}
              </Grid>
            )}
            {userName ? (
              <Grid item lg={4} className="user-dropdown">
                <Box
                  aria-controls="fade-menu"
                  onClick={handleClick}
                  data-testid="headerNavMenu"
                  justifyContent="center"
                  alignItems="center"
                  className={`header-nav-menu ${anchorEl ? 'active' : ''}`}
                >
                  <Box
                    margin={{ right: 'xxs' }}
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="row"
                  >
                    <img src={`${S3_ASSETS_PATH}/images/wing.svg`} />
                  </Box>
                  <span>{userName}</span>
                </Box>
                <Menu
                  id="fade-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  open={flag}
                  onClose={handleClose}
                  TransitionComponent={Fade}
                  className="header-drop-down"
                  // classes={{root : styles.root}}
                >
                  {active ? (
                    <RoleGuard feature="profile.view">
                      <MenuItem
                        className="menu-item"
                        onClick={() => history.push('/profile')}
                        data-testid="headerNavMenuProfileLink"
                      >
                        View CSP Profile
                      </MenuItem>
                    </RoleGuard>
                  ) : null}
                  <MenuItem
                    onClick={handleLogout}
                    data-testid="headerNavMenuLogoutLink"
                  >
                    Logout
                  </MenuItem>
                </Menu>
              </Grid>
            ) : (
              ''
            )}
          </Grid>
        </Container>
      </header>
    </Fragment>
  ) : isRegistrationFlow || config.useTopBarV2 ? (
    <TopBarV2
      authenticated={!!userName}
      portal="csp"
      username={userName}
      title={title}
      subText={note}
      menus={menus}
      onLogoClick={() => history.push('/')}
    />
  ) : (
    <TopBar
      authenticated={!!userName}
      portal="csp"
      username={userName}
      title={title}
      subText={note}
      menus={menus}
    />
  );
};

export default Header;
